/**
 * OversightChrome — tiles and the segmented switch, from "Company Today" 7a.
 * @file src/web/features/oversight/components/OversightChrome.tsx
 *
 * @llm-rule WHEN: Framing the Company Admin/CTO oversight screens
 * @llm-rule NOTE: The border weight is the signal. A solid frame is a plain
 *   count, a heavy accent frame is the number that needs attention, a dashed
 *   frame is context. That reads at a glance without colour alone doing the
 *   work.
 */

import type { ReactNode } from 'react';

export type TileTone = 'plain' | 'alert' | 'quiet';

export function StatTiles({
  tiles,
}: {
  tiles: Array<{ n: ReactNode; label: string; tone?: TileTone }>;
}) {
  return (
    <div className="mb-3 flex gap-2">
      {tiles.map((tile) => {
        const alert = tile.tone === 'alert';
        const quiet = tile.tone === 'quiet';
        return (
          <div
            key={tile.label}
            className="flex flex-1 flex-col rounded-[12px] px-3 py-2.5"
            style={{
              border: alert
                ? '2.5px solid var(--dot-flag)'
                : quiet
                  ? '2px dashed var(--rule)'
                  : '2px solid var(--ink)',
              background: quiet ? 'transparent' : 'var(--paper)',
            }}
          >
            <span
              className="font-clock text-[20px] font-bold leading-none"
              style={{ color: alert ? 'var(--dot-flag)' : 'var(--ink)' }}
            >
              {tile.n}
            </span>
            <span className="mt-1.5 text-[11.5px] leading-tight text-ink-faint">{tile.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Two halves in one pill. Not UIKit's Tabs: the design draws a single bordered
 * capsule where the active half is filled, and the count badge changes colour
 * with it — that reads as one control with two states rather than two buttons.
 */
export function SegmentedSwitch<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ key: T; label: string; count: number }>;
  onChange: (key: T) => void;
}) {
  return (
    <div
      className="mb-4 flex overflow-hidden rounded-full"
      style={{ border: '2px solid var(--ink)' }}
      role="tablist"
    >
      {options.map((option) => {
        const on = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(option.key)}
            className="flex flex-1 items-center justify-center gap-2 py-2 text-[13.5px] font-medium transition-colors"
            style={{
              background: on ? 'var(--ink)' : 'transparent',
              color: on ? 'var(--paper)' : 'var(--ink-faint)',
            }}
          >
            {option.label}
            <span
              className="font-clock rounded-full px-1.5 text-[10.5px] font-bold"
              style={{
                background: on ? 'var(--dot-flag)' : 'var(--paper-sunk)',
                color: on ? '#fff' : 'var(--ink-faint)',
              }}
            >
              {option.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** The count that sits at the end of a person row. */
export function FlagCount({ n }: { n: number }) {
  return (
    <span
      className="font-clock flex-none rounded-full px-2.5 py-0.5 text-[12px] font-bold"
      style={{
        background: n > 0 ? 'var(--state-bad-bg)' : 'var(--paper-sunk)',
        color: n > 0 ? 'var(--dot-flag)' : 'var(--ink-faint)',
      }}
    >
      {n}
    </span>
  );
}
